import { http } from '@/utils/http.js';
import { devPath, orgPath } from '@/utils/paths.js';
import { singular } from 'pluralize';

export const MetricsApi = {
  travel(query) {
    return http.get(`${orgPath()}/charts/travel`, { params: query }).then((response) => response.data);
  },

  spend(query, signal) {
    return http.get(`${orgPath()}/charts/spend`, { params: query, signal }).then((response) => {
      response.data.data = transformSpend(response.data?.data, query);
      return response.data;
    });
  },

  bookings(query, signal) {
    return http.get(`${orgPath()}/charts/bookings`, { params: query, signal }).then((response) => {
      response.data.data = transformBookings(response.data?.data);
      return response.data;
    });
  },

  bookingsSegment(query, signal) {
    return http.get(`${orgPath()}/charts/segment-bookings`, { params: query, signal }).then((response) => {
      let output = {
        total: response.data?.segments_unique || 0,
        total_segments: response.data?.segments || 0,
        tmc: [],
        direct: [],
        combined: [],
      };

      response.data?.data.forEach((item) => {
        let tmc = item.tmc_unique || 0;
        let direct = item.direct_unique || 0;

        output.tmc.push({
          x: item.interval,
          y: tmc,
        });

        output.direct.push({
          x: item.interval,
          y: direct,
        });

        output.combined.push({
          x: item.interval,
          y: tmc + direct,
        });
      });

      response.data = output;
      return response.data;
    });
  },

  bookingsHotelNights(query, signal) {
    return http.get(`${orgPath()}/charts/hotel-nights`, { params: query, signal }).then((response) => {
      let output = {
        total: response.data?.nights || 0,
        tmc: [],
        direct: [],
        combined: [],
      };

      response.data?.data.forEach((item) => {
        let tmc = item.tmc || 0;
        let direct = item.direct || 0;

        output.tmc.push({
          x: item.interval,
          y: tmc,
        });

        output.direct.push({
          x: item.interval,
          y: direct,
        });

        output.combined.push({
          x: item.interval,
          y: tmc + direct,
        });
      });

      response.data = output;
      return response.data;
    });
  },

  segmentCosts(query, signal) {
    return http.get(`${orgPath()}/charts/segment-costs`, { params: query, signal }).then((response) => {
      let amount = response.data?.computed_ppu || 0;
      let output = {
        total: Math.round(amount * 100) / 100,
        tmc: [],
        direct: [],
        combined: [],
      };

      response.data?.data.forEach((item) => {
        let tmc = item.tmc || 0;
        let direct = item.direct || 0;

        output.tmc.push({
          x: item.interval,
          y: tmc,
        });

        output.direct.push({
          x: item.interval,
          y: direct,
        });

        output.combined.push({
          x: item.interval,
          // average of both numbers
          y: (tmc + direct) / 2,
        });
      });

      response.data = output;
      return response.data;
    });
  },

  segmentSources(query, colors, signal) {
    return http.get(`${orgPath()}/charts/segment-sources`, { params: query, signal }).then((response) => {
      let total_tmc = (response.data?.data?.tmc?.computed_spend || 0) / 100;
      let total_direct = (response.data?.data?.direct?.computed_spend || 0) / 100;

      let output = {
        source_types: [
          { name: 'TMC', y: total_tmc, color: colors.tmc },
          { name: 'Non-TMC', y: total_direct, color: colors.direct },
        ],
        sources: [],
      };

      output.sources = [
        ...mapSegmentSources(response.data?.data?.tmc?.sources),
        ...mapSegmentSources(response.data?.data?.direct?.sources),
      ];

      output.sources = sortByValueOrId(output.sources);
      response.data = output;
      return response.data;
    });

    function mapSegmentSources(sources = []) {
      if (!Array.isArray(sources)) {
        return [];
      }

      return sources.map((item) => {
        let name = item.source_name;
        let id = item.source;
        let classification = item.class?.toLowerCase();

        // Use the shortened form of this source
        if (id === 'AmexGBT') {
          name = id;
        }

        if (id === 'Email') {
          name = 'Unknown';
        }

        // If we don't have a source at all then mark it as unknown
        if (!id) {
          name = 'Unknown';
          classification = 'unknown';
        }

        // If we don't have a source name but we do have a source just use that instead
        if (!name && Boolean(id)) {
          name = id;
        }

        return {
          id: id,
          name: name,
          y: item.computed_spend / 100,
          color: item.class === 'TMC' ? colors.tmc : colors.direct,
          class: classification,
        };
      });
    }
  },

  segmentPurchase(query, signal) {
    return http.get(`${orgPath()}/charts/segment-purchase`, { params: query, signal }).then((response) => {
      let output = {
        total: response.data.avg_diff,
        data: [],
      };

      output.data = response.data?.data.map((item) => {
        return {
          x: item.interval,
          y: item.avg_diff,
          segments: item.segments_unique,
        };
      });

      response.data = output;
      return response.data;
    });
  },

  parses(query) {
    return http.get(`${devPath()}/emails/all`, { params: query }).then((response) => {
      // remove extraneous wrapper object
      return response.data.map((metric) => {
        const label = metric.data.dataSet.label;

        // Add label to each data point and singularize it as needed
        metric.data.dataSet.data = metric.data.dataSet.data.map((point) => {
          point.type = point.value === 1 ? singular(label) : label;
          return point;
        });

        return metric.data;
      });
    });
  },
};

function transformSpend(data = []) {
  let output = {
    direct: [],
    tmc: [],
  };

  if (!data.length) {
    return output;
  }

  let sumDirect = 0;
  let sumTmc = 0;
  data.forEach((item) => {
    sumDirect += item.direct?.total || 0;
    sumTmc += item.tmc?.total || 0;
    output.direct.push([item.interval, sumDirect / 100]);
    output.tmc.push([item.interval, sumTmc / 100]);
  });

  return output;
}

function transformBookings(data = {}) {
  let output = {
    direct: [],
    tmc: [],
  };

  ['air', 'hotel', 'car', 'rail'].forEach((category) => {
    output.direct.push(data[category]?.direct?.unique || 0);
    output.tmc.push(data[category]?.tmc?.unique || 0);
  });

  return output;
}

function sortByValueOrId(arr) {
  return [...arr].sort((a, b) => {
    // First, sort by number value descending
    if (a.y < b.y) {
      return 1;
    } else if (a.y > b.y) {
      return -1;
    }

    // Second, if we're here, they are the same number
    // so let's now sort alphabetically ascending
    let aId = a.id.toLowerCase();
    let bId = b.id.toLowerCase();
    if (aId > bId) {
      return 1;
    } else if (aId < bId) {
      return -1;
    }

    // Third, both the number and alpha is the
    // same so just leave the sort as it is.
    return 0;
  });
}
